import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@utils/cn';

const buttonVariants = cva(
  'inline-flex min-h-11 items-center justify-center gap-2 whitespace-normal rounded-full text-label font-medium transition-[color,background-color,border-color,transform] duration-fast ease-out active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 disabled:active:scale-100 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        /** High-emphasis action; navigation and switch accents may coexist. */
        primary:
          'bg-primary text-primary-foreground [-webkit-text-fill-color:var(--primary-foreground)] hover:bg-primary/90',
        /** Neutral fallback. Buttons must opt in to the brand fill explicitly. */
        default:
          'bg-secondary text-secondary-foreground hover:bg-accent hover:text-accent-foreground',
        /** Inverted pill. Useful for neutral high-contrast selection. */
        secondary:
          'rounded-full bg-foreground text-background hover:bg-foreground/90',
        outline:
          'border border-border bg-transparent text-secondary-foreground hover:bg-accent hover:text-accent-foreground',
        ghost:
          'text-secondary-foreground hover:bg-accent hover:text-accent-foreground',
        destructive: 'bg-secondary text-destructive hover:bg-accent',
        link: 'text-brand-text hover:opacity-80'
      },
      size: {
        default: 'min-h-11 px-5 py-2.5',
        sm: 'min-h-11 px-4 py-2 text-caption',
        lg: 'min-h-12 px-8 py-3',
        icon: 'size-11 shrink-0'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
